// Phase 72: renders admin-defined CustomFields at one of three
// placements: "before_design" (above the placement editor),
// "after_design" (between placements + approval), or "order_level"
// (next to OrderAddOnsSection). The fourth placement, "with_spot",
// renders INSIDE PlacementSection's spot editor and is owned there,
// not here.
//
// The section is a thin wrapper that:
//   - filters config.customFields to the requested placement,
//   - evaluates each field's conditionalRule against the current
//     fieldValues map, hiding fields that fail,
//   - delegates rendering to CustomFieldRenderer.

import type { Config } from "../types";
import { evaluateCondition, type FieldValues } from "../util/conditions";
import { CustomFieldRenderer, type FieldValue } from "../components/fields/CustomFieldRenderer";

type Placement = "before_design" | "after_design" | "order_level" | "with_spot";

export function CustomFieldsSection({
  config,
  placement,
  fieldValues,
  onChange,
}: {
  config: Config;
  placement: Placement;
  fieldValues: FieldValues;
  /** Patch one field. The parent owns the full map. */
  onChange: (slug: string, value: FieldValue) => void;
}) {
  const fields = (config.customFields ?? []).filter(
    (f) => f.placement === placement,
  );
  if (fields.length === 0) return null;

  // Visible fields: apply conditional rules. A hidden field is treated
  // as "not set" for pricing + cart property emission elsewhere; the
  // values map still carries its value if it was set before becoming
  // hidden (so toggling back doesn't drop input).
  const visible = fields.filter((f) =>
    evaluateCondition(f.conditionalRule, fieldValues),
  );
  if (visible.length === 0) return null;

  return (
    <section
      className={`rk-pc-sec rk-pc-custom-fields rk-pc-custom-fields--${placement}`}
    >
      <div className="rk-pc-custom-fields__list">
        {visible.map((f) => (
          <CustomFieldRenderer
            key={f.id}
            field={f}
            value={fieldValues[f.slug]}
            onChange={(v) => onChange(f.slug, v)}
          />
        ))}
      </div>
    </section>
  );
}
